// ═══════════════════════════════════════════
// EMH Attendance System — Direct Firebase Write
// ═══════════════════════════════════════════
//
// CRITICAL: All functions bypass SyncContext entirely.
// Writes directly to Firebase via get/set/update.
// Components set up onValue listeners for real-time updates.
//
// Path: /attendance/{YYYY-MM}/{user_id}/{date}
// This structure:
// - Groups by month for efficient monthly queries
// - {user_id}/{date} prevents duplicate clock-ins structurally

import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebase/config';

/**
 * Get today's date string in YYYY-MM-DD format
 */
function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get current time string in HH:MM format
 */
function getCurrentTime() {
  return new Date().toTimeString().slice(0, 5);
}

/**
 * Clock in: SET a new attendance record.
 * Path: /attendance/YYYY-MM/userId/date
 *
 * @param {Object} params
 * @param {string} params.userId - e.g. "U002"
 * @param {string} params.userName - e.g. "柚木謙一"
 * @returns {Object} { date, clockIn, record, alreadyExists }
 */
export async function clockIn({ userId, userName }) {
  const todayStr = getTodayStr();
  const month = todayStr.slice(0, 7);
  const clockInTime = getCurrentTime();
  const path = `attendance/${month}/${userId}/${todayStr}`;

  // Check if already clocked in today
  const existing = await get(ref(database, path));
  if (existing.exists()) {
    return { date: todayStr, clockIn: existing.val().clock_in, record: existing.val(), alreadyExists: true };
  }

  const record = {
    user_id: userId,
    user_name: userName,
    date: todayStr,
    clock_in: clockInTime,
    clock_out: null,
    status: 'pending',
    confirmed_by: null,
    confirmed_at: null,
    hours: null,
    note: '',
    created_at: new Date().toISOString(),
  };

  await set(ref(database, path), record);

  return { date: todayStr, clockIn: clockInTime, record, alreadyExists: false };
}

/**
 * Clock out: UPDATE clock_out + hours on existing record.
 *
 * @param {Object} params
 * @param {string} params.userId
 * @returns {Object} { clockOut, hours }
 */
export async function clockOut({ userId }) {
  const todayStr = getTodayStr();
  const month = todayStr.slice(0, 7);
  const clockOutTime = getCurrentTime();
  const path = `attendance/${month}/${userId}/${todayStr}`;

  // Get existing record to calculate hours
  const snap = await get(ref(database, path));
  if (!snap.exists()) {
    throw new Error('No clock-in record found for today');
  }

  const record = snap.val();
  const [inH, inM] = record.clock_in.split(':').map(Number);
  const [outH, outM] = clockOutTime.split(':').map(Number);
  const hours = Math.round(((outH * 60 + outM) - (inH * 60 + inM)) / 60 * 10) / 10;

  await update(ref(database, path), {
    clock_out: clockOutTime,
    hours: hours,
  });

  return { clockOut: clockOutTime, hours };
}

/**
 * Get today's record for a specific user.
 *
 * @param {string} userId
 * @returns {Object|null} attendance record or null
 */
export async function getTodayRecord(userId) {
  const todayStr = getTodayStr();
  const month = todayStr.slice(0, 7);
  const path = `attendance/${month}/${userId}/${todayStr}`;

  const snap = await get(ref(database, path));
  return snap.exists() ? snap.val() : null;
}

/**
 * Confirm a single attendance record (admin action).
 *
 * @param {Object} params
 * @param {string} params.userId - staff user_id
 * @param {string} params.date - YYYY-MM-DD
 * @param {string} params.month - YYYY-MM
 * @param {string} params.adminId - admin user_id confirming
 */
export async function confirmAttendance({ userId, date, month, adminId }) {
  const path = `attendance/${month}/${userId}/${date}`;
  await update(ref(database, path), {
    status: 'confirmed',
    confirmed_by: adminId,
    confirmed_at: new Date().toISOString(),
  });
}

/**
 * Confirm all pending records for a given month (admin action).
 *
 * @param {Object} params
 * @param {string} params.month - YYYY-MM format
 * @param {string} params.adminId - admin user_id confirming
 */
export async function confirmAllPending({ month, adminId }) {
  const path = `attendance/${month}`;
  const snap = await get(ref(database, path));
  if (!snap.exists()) return;

  const data = snap.val();
  const updates = {};
  const now = new Date().toISOString();

  Object.entries(data).forEach(([userId, dates]) => {
    if (dates && typeof dates === 'object') {
      Object.entries(dates).forEach(([date, record]) => {
        if (record && record.status === 'pending') {
          updates[`attendance/${month}/${userId}/${date}/status`] = 'confirmed';
          updates[`attendance/${month}/${userId}/${date}/confirmed_by`] = adminId;
          updates[`attendance/${month}/${userId}/${date}/confirmed_at`] = now;
        }
      });
    }
  });

  if (Object.keys(updates).length > 0) {
    await update(ref(database, '/'), updates);
  }
}

/**
 * Build the Firebase ref path for real-time listener.
 *
 * @param {string} month - YYYY-MM format
 * @param {string} [userId] - optional, to listen to specific user
 * @returns {string} Firebase path
 */
export function getAttendancePath(month, userId) {
  if (userId) return `attendance/${month}/${userId}`;
  return `attendance/${month}`;
}
